import { useEffect, useState } from 'react';
import { Plus, Trash2, Sparkles, Search } from 'lucide-react';
import Input from '@/components/ui/Input';
import Textarea from '@/components/ui/Textarea';
import Select from '@/components/ui/Select';
import Button from '@/components/ui/Button';
import ImageUploader from '@/components/admin/ImageUploader';
import AiForgeButton from '@/components/admin/AiForgeButton';
import { assetTarget, assetPrompt } from '@/config/asset-specs';
import StockPhotoPicker from '@/components/admin/StockPhotoPicker';
import api from '@/lib/api';
import { tierInterval, tierIsFeatured } from '@/lib/pricing-tier';
import { statNumber } from '@/lib/stat-number';

/**
 * Image field whose crop ratio, recommended size and AI prompt all come from
 * the asset-spec registry (config/asset-specs.ts) rather than being repeated
 * here. `specKey` is the slot; pass targetWidth/Height only to override.
 */
function ImageUploaderWithAI({
  label,
  value,
  onChange,
  targetWidth,
  targetHeight,
  specKey,
}: {
  label: string;
  value: string;
  onChange: (url: string) => void;
  targetWidth?: number;
  targetHeight?: number;
  specKey?: string;
}) {
  const [aiOpen, setAiOpen] = useState(false);
  const [stockOpen, setStockOpen] = useState(false);
  const spec = specKey ? assetTarget(specKey) : undefined;
  const width = targetWidth ?? spec?.width;
  const height = targetHeight ?? spec?.height;
  // Prompts are built from THIS site's name and tagline, so the suggestion on a
  // lodging or museum tenant isn't seed-company copy.
  const aiPrompt = specKey ? assetPrompt(specKey) : undefined;

  return (
    <div className="space-y-2">
      <ImageUploader label={label} value={value} onChange={onChange} targetWidth={width} targetHeight={height} />
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" size="sm" onClick={() => setStockOpen(true)} type="button">
          <Search className="h-4 w-4 mr-1" aria-hidden="true" />
          Stock photo
        </Button>
        <Button variant="outline" size="sm" onClick={() => setAiOpen(true)} type="button">
          <Sparkles className="h-4 w-4 mr-1" aria-hidden="true" />
          AI Generate
        </Button>
      </div>
      <StockPhotoPicker
        isOpen={stockOpen}
        onClose={() => setStockOpen(false)}
        folder="homepage"
        targetWidth={width}
        targetHeight={height}
        initialQuery={label.toLowerCase()}
        onPick={({ url }) => onChange(url)}
      />
      <AiForgeButton
        isOpen={aiOpen}
        onClose={() => setAiOpen(false)}
        context="homepage"
        contextName={label.toLowerCase()}
        initialPrompt={aiPrompt}
        onImageGenerated={({ url }) => { onChange(url); setAiOpen(false); }}
      />
    </div>
  );
}

interface SectionConfigEditorProps {
  sectionType: string;
  config: any;
  onChange: (config: any) => void;
}

function HeroConfig({ config, onChange }: { config: any; onChange: (c: any) => void }) {
  return (
    <div className="space-y-4">
      <ImageUploaderWithAI label="Hero Image" value={config.image_url || ''} onChange={(url) => onChange({ ...config, image_url: url })} specKey="hero.image_url" />
      <Select
        label="Overlay Style"
        value={config.overlay_style || 'warm'}
        onChange={(e) => onChange({ ...config, overlay_style: e.target.value })}
        options={[
          { label: 'Warm', value: 'warm' },
          { label: 'Dark', value: 'dark' },
          { label: 'None', value: 'none' },
        ]}
      />
      <Input label="CTA Text" value={config.cta_text || ''} onChange={(e) => onChange({ ...config, cta_text: e.target.value })} />
      <Input label="CTA Link" value={config.cta_link || ''} onChange={(e) => onChange({ ...config, cta_link: e.target.value })} placeholder="/shop" />
      <Select
        label="Text Alignment"
        value={config.text_align || 'center'}
        onChange={(e) => onChange({ ...config, text_align: e.target.value })}
        options={[
          { label: 'Center', value: 'center' },
          { label: 'Left', value: 'left' },
          { label: 'Right', value: 'right' },
        ]}
      />
    </div>
  );
}

function StoryConfig({ config, onChange }: { config: any; onChange: (c: any) => void }) {
  return (
    <div className="space-y-4">
      <ImageUploaderWithAI label="Story Image" value={config.image_url || ''} onChange={(url) => onChange({ ...config, image_url: url })} specKey="story.image_url" />
      <Input label="Image Alt Text" value={config.image_alt || ''} onChange={(e) => onChange({ ...config, image_alt: e.target.value })} />
      <Select
        label="Image Position"
        value={config.image_position || 'left'}
        onChange={(e) => onChange({ ...config, image_position: e.target.value })}
        options={[
          { label: 'Left', value: 'left' },
          { label: 'Right', value: 'right' },
        ]}
      />
      <Textarea label="Body Text" value={config.body || ''} onChange={(e) => onChange({ ...config, body: e.target.value })} rows={5} />
      <Input label="CTA Text" value={config.cta_text || ''} onChange={(e) => onChange({ ...config, cta_text: e.target.value })} helperText="Optional" />
      <Input label="CTA Link" value={config.cta_link || ''} onChange={(e) => onChange({ ...config, cta_link: e.target.value })} helperText="Optional" />
    </div>
  );
}

function ImageBannerConfig({ config, onChange }: { config: any; onChange: (c: any) => void }) {
  return (
    <div className="space-y-4">
      <ImageUploaderWithAI label="Banner Image" value={config.image_url || ''} onChange={(url) => onChange({ ...config, image_url: url })} specKey="image_banner.image_url" />
      <Input label="Overlay Text" value={config.overlay_text || ''} onChange={(e) => onChange({ ...config, overlay_text: e.target.value })} />
      <Input label="CTA Text" value={config.cta_text || ''} onChange={(e) => onChange({ ...config, cta_text: e.target.value })} helperText="Optional" />
      <Input label="CTA Link" value={config.cta_link || ''} onChange={(e) => onChange({ ...config, cta_link: e.target.value })} helperText="Optional" />
    </div>
  );
}

function FeaturedProductsConfig({ config, onChange }: { config: any; onChange: (c: any) => void }) {
  return (
    <div className="space-y-4">
      <Input
        label="Product Count"
        type="number"
        value={config.product_count || 8}
        onChange={(e) => onChange({ ...config, product_count: Number(e.target.value) })}
        min={1}
        max={20}
      />
      <Select
        label="Display Style"
        value={config.display_style || 'grid'}
        onChange={(e) => onChange({ ...config, display_style: e.target.value })}
        options={[
          { label: 'Grid', value: 'grid' },
          { label: 'Carousel', value: 'carousel' },
        ]}
      />
    </div>
  );
}

function SeasonalSpotlightConfig({ config, onChange }: { config: any; onChange: (c: any) => void }) {
  return (
    <div className="space-y-4">
      <Input label="Season Name" value={config.season_name || ''} onChange={(e) => onChange({ ...config, season_name: e.target.value })} placeholder="Spring 2026" />
      <Textarea label="Description" value={config.description || ''} onChange={(e) => onChange({ ...config, description: e.target.value })} rows={3} />
      <ImageUploaderWithAI label="Season Image" value={config.image_url || ''} onChange={(url) => onChange({ ...config, image_url: url })} specKey="seasonal_spotlight.image_url" />
      <Input label="Image Alt Text" value={config.image_alt || ''} onChange={(e) => onChange({ ...config, image_alt: e.target.value })} />
      <Textarea
        label="Product IDs (comma-separated UUIDs)"
        value={(config.product_ids || []).join(', ')}
        onChange={(e) => onChange({ ...config, product_ids: e.target.value.split(',').map((s: string) => s.trim()).filter(Boolean) })}
        rows={2}
        helperText="Enter product UUIDs separated by commas"
      />
      <Input label="CTA Text" value={config.cta_text || ''} onChange={(e) => onChange({ ...config, cta_text: e.target.value })} />
      <Input label="CTA Link" value={config.cta_link || ''} onChange={(e) => onChange({ ...config, cta_link: e.target.value })} />
    </div>
  );
}

function ValuePropsConfig({ config, onChange }: { config: any; onChange: (c: any) => void }) {
  const items: any[] = config.items || [];

  const ICON_OPTIONS = [
    { label: 'Leaf', value: 'Leaf' },
    { label: 'Truck', value: 'Truck' },
    { label: 'Award', value: 'Award' },
    { label: 'Sprout', value: 'Sprout' },
    { label: 'ShieldCheck', value: 'ShieldCheck' },
    { label: 'Heart', value: 'Heart' },
    { label: 'Mail', value: 'Mail' },
  ];

  function updateItem(index: number, field: string, value: string) {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], [field]: value };
    onChange({ ...config, items: newItems });
  }

  function addItem() {
    if (items.length >= 4) return;
    onChange({ ...config, items: [...items, { icon: 'Leaf', title: '', description: '' }] });
  }

  function removeItem(index: number) {
    onChange({ ...config, items: items.filter((_, i) => i !== index) });
  }

  return (
    <div className="space-y-4">
      {items.map((item: any, i: number) => (
        <div key={i} className="border border-gray-200 rounded-lg p-4 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">Item {i + 1}</span>
            <Button variant="ghost" size="sm" onClick={() => removeItem(i)} aria-label={`Remove item ${i + 1}`}>
              <Trash2 className="h-4 w-4 text-red-500" aria-hidden="true" />
            </Button>
          </div>
          <Select label="Icon" value={item.icon || 'Leaf'} onChange={(e) => updateItem(i, 'icon', e.target.value)} options={ICON_OPTIONS} />
          <Input label="Title" value={item.title || ''} onChange={(e) => updateItem(i, 'title', e.target.value)} />
          <Input label="Description" value={item.description || ''} onChange={(e) => updateItem(i, 'description', e.target.value)} />
        </div>
      ))}
      {items.length < 4 && (
        <Button variant="ghost" size="sm" onClick={addItem}>
          <Plus className="h-4 w-4 mr-1" aria-hidden="true" />
          Add Item
        </Button>
      )}
    </div>
  );
}

function ImageGalleryConfig({ config, onChange }: { config: any; onChange: (c: any) => void }) {
  const images: any[] = config.images || [];

  function updateImage(index: number, field: string, value: string) {
    const newImages = [...images];
    newImages[index] = { ...newImages[index], [field]: value };
    onChange({ ...config, images: newImages });
  }

  function addImage() {
    onChange({ ...config, images: [...images, { url: '', alt: '', link: '' }] });
  }

  function removeImage(index: number) {
    onChange({ ...config, images: images.filter((_, i) => i !== index) });
  }

  return (
    <div className="space-y-4">
      {images.map((img: any, i: number) => (
        <div key={i} className="border border-gray-200 rounded-lg p-4 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">Image {i + 1}</span>
            <Button variant="ghost" size="sm" onClick={() => removeImage(i)} aria-label={`Remove image ${i + 1}`}>
              <Trash2 className="h-4 w-4 text-red-500" aria-hidden="true" />
            </Button>
          </div>
          <ImageUploader label={`Image ${i + 1}`} value={img.url || ''} onChange={(url) => updateImage(i, 'url', url)} {...assetTarget('image_gallery.images')} />
          <Input label="Alt Text" value={img.alt || ''} onChange={(e) => updateImage(i, 'alt', e.target.value)} />
          <Input label="Link (optional)" value={img.link || ''} onChange={(e) => updateImage(i, 'link', e.target.value)} />
        </div>
      ))}
      <Button variant="ghost" size="sm" onClick={addImage}>
        <Plus className="h-4 w-4 mr-1" aria-hidden="true" />
        Add Image
      </Button>
    </div>
  );
}

function BannerConfig({ config, onChange }: { config: any; onChange: (c: any) => void }) {
  const BG_OPTIONS = [
    { label: 'Sage 700', value: 'bg-sage-700' },
    { label: 'Sage 800', value: 'bg-sage-800' },
    { label: 'Sage 900', value: 'bg-sage-900' },
    { label: 'Terra', value: 'bg-terra' },
    { label: 'Amber 700', value: 'bg-amber-700' },
    { label: 'Stone 800', value: 'bg-stone-800' },
  ];

  return (
    <div className="space-y-4">
      <Select
        label="Background Color"
        value={config.bg_color || 'bg-sage-700'}
        onChange={(e) => onChange({ ...config, bg_color: e.target.value })}
        options={BG_OPTIONS}
      />
      <Input label="CTA Text" value={config.cta_text || ''} onChange={(e) => onChange({ ...config, cta_text: e.target.value })} helperText="Optional" />
      <Input label="CTA Link" value={config.cta_link || ''} onChange={(e) => onChange({ ...config, cta_link: e.target.value })} helperText="Optional" />
    </div>
  );
}

function GrowingTipsConfig({ config, onChange }: { config: any; onChange: (c: any) => void }) {
  const tips: any[] = config.tips || [];

  function updateTip(index: number, field: string, value: string) {
    const newTips = [...tips];
    newTips[index] = { ...newTips[index], [field]: value };
    onChange({ ...config, tips: newTips });
  }

  function addTip() {
    onChange({ ...config, tips: [...tips, { title: '', body: '', link: '' }] });
  }

  function removeTip(index: number) {
    onChange({ ...config, tips: tips.filter((_, i) => i !== index) });
  }

  return (
    <div className="space-y-4">
      {tips.map((tip: any, i: number) => (
        <div key={i} className="border border-gray-200 rounded-lg p-4 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">Tip {i + 1}</span>
            <Button variant="ghost" size="sm" onClick={() => removeTip(i)} aria-label={`Remove tip ${i + 1}`}>
              <Trash2 className="h-4 w-4 text-red-500" aria-hidden="true" />
            </Button>
          </div>
          <Input label="Title" value={tip.title || ''} onChange={(e) => updateTip(i, 'title', e.target.value)} />
          <Textarea label="Body" value={tip.body || ''} onChange={(e) => updateTip(i, 'body', e.target.value)} rows={3} />
          <Input label="Link (optional)" value={tip.link || ''} onChange={(e) => updateTip(i, 'link', e.target.value)} placeholder="/blog/my-post" />
        </div>
      ))}
      <Button variant="ghost" size="sm" onClick={addTip}>
        <Plus className="h-4 w-4 mr-1" aria-hidden="true" />
        Add Tip
      </Button>
    </div>
  );
}

function TestimonialsConfig({ config, onChange }: { config: any; onChange: (c: any) => void }) {
  const testimonials: any[] = config.testimonials || [];

  function updateTestimonial(index: number, field: string, value: string | number) {
    const newItems = [...testimonials];
    newItems[index] = { ...newItems[index], [field]: value };
    onChange({ ...config, testimonials: newItems });
  }

  function addTestimonial() {
    onChange({
      ...config,
      testimonials: [...testimonials, {
        id: Date.now().toString(),
        first_name: '',
        last_initial: '',
        rating: 5,
        title: '',
        body: '',
        is_verified_purchase: false,
        product_name: '',
        product_slug: '',
      }],
    });
  }

  function removeTestimonial(index: number) {
    onChange({ ...config, testimonials: testimonials.filter((_, i) => i !== index) });
  }

  return (
    <div className="space-y-4">
      {testimonials.map((item: any, i: number) => (
        <div key={i} className="border border-gray-200 rounded-lg p-4 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">Testimonial {i + 1}</span>
            <Button variant="ghost" size="sm" onClick={() => removeTestimonial(i)} aria-label={`Remove testimonial ${i + 1}`}>
              <Trash2 className="h-4 w-4 text-red-500" aria-hidden="true" />
            </Button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input label="First Name" value={item.first_name || ''} onChange={(e) => updateTestimonial(i, 'first_name', e.target.value)} />
            <Input label="Last Initial" value={item.last_initial || ''} onChange={(e) => updateTestimonial(i, 'last_initial', e.target.value)} />
          </div>
          <Select
            label="Rating"
            value={String(item.rating || 5)}
            onChange={(e) => updateTestimonial(i, 'rating', Number(e.target.value))}
            options={[
              { label: '5 Stars', value: '5' },
              { label: '4 Stars', value: '4' },
              { label: '3 Stars', value: '3' },
              { label: '2 Stars', value: '2' },
              { label: '1 Star', value: '1' },
            ]}
          />
          <Input label="Review Title" value={item.title || ''} onChange={(e) => updateTestimonial(i, 'title', e.target.value)} helperText="Optional" />
          <Textarea label="Review Body" value={item.body || ''} onChange={(e) => updateTestimonial(i, 'body', e.target.value)} rows={3} />
          <div className="grid grid-cols-2 gap-3">
            <Input label="Product Name" value={item.product_name || ''} onChange={(e) => updateTestimonial(i, 'product_name', e.target.value)} helperText="Optional" />
            <Input label="Product Slug" value={item.product_slug || ''} onChange={(e) => updateTestimonial(i, 'product_slug', e.target.value)} helperText="Optional" />
          </div>
        </div>
      ))}
      <Button variant="ghost" size="sm" onClick={addTestimonial}>
        <Plus className="h-4 w-4 mr-1" aria-hidden="true" />
        Add Testimonial
      </Button>
    </div>
  );
}

function PricingTiersConfig({ config, onChange }: { config: any; onChange: (c: any) => void }) {
  const tiers: any[] = config.tiers || [];

  function updateTier(index: number, field: string, value: any) {
    const newTiers = [...tiers];
    newTiers[index] = { ...newTiers[index], [field]: value };
    onChange({ ...config, tiers: newTiers });
  }

  function addTier() {
    onChange({ ...config, tiers: [...tiers, { name: '', price: '', interval: 'month', description: '', features: [], cta_text: '', cta_link: '', featured: false }] });
  }

  // The stored shape is the one the wizard writes and the storefront reads:
  // `features` is an ARRAY, plus `interval`, `featured`, `cta_text`/`cta_link`.
  // This editor used to write `features` as one string (which crashed the
  // homepage section), and `period` / `highlighted`, which nothing rendered.
  // Lines are kept as typed while editing — blank ones are dropped at render —
  // so pressing Enter in the box still starts a new line.
  function featuresText(tier: any): string {
    return Array.isArray(tier.features) ? tier.features.join('\n') : tier.features || '';
  }

  function setFeatured(index: number, value: boolean) {
    const newTiers = [...tiers];
    // Retire the older spellings so a stale `highlighted: true` can't keep a
    // tier marked after it is unticked here.
    const { highlighted: _h, isPopular: _p, ...rest } = newTiers[index];
    newTiers[index] = { ...rest, featured: value };
    onChange({ ...config, tiers: newTiers });
  }

  function removeTier(index: number) {
    onChange({ ...config, tiers: tiers.filter((_, i) => i !== index) });
  }

  return (
    <div className="space-y-4">
      {tiers.map((tier: any, i: number) => (
        <div key={i} className="border border-gray-200 rounded-lg p-4 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">Tier {i + 1}</span>
            <Button variant="ghost" size="sm" onClick={() => removeTier(i)} aria-label={`Remove tier ${i + 1}`}>
              <Trash2 className="h-4 w-4 text-red-500" aria-hidden="true" />
            </Button>
          </div>
          <Input label="Name" value={tier.name || ''} onChange={(e) => updateTier(i, 'name', e.target.value)} />
          <div className="grid grid-cols-2 gap-3">
            <Input label="Price" value={tier.price || ''} onChange={(e) => updateTier(i, 'price', e.target.value)} placeholder="29.99" />
            <Input label="Period" value={tierInterval(tier)} onChange={(e) => updateTier(i, 'interval', e.target.value)} placeholder="month" />
          </div>
          <Input label="Description" value={tier.description || ''} onChange={(e) => updateTier(i, 'description', e.target.value)} helperText="Optional — shown on the pricing page" />
          <Textarea label="Features (one per line)" value={featuresText(tier)} onChange={(e) => updateTier(i, 'features', e.target.value.split('\n'))} rows={5} />
          <div className="grid grid-cols-2 gap-3">
            <Input label="CTA Text" value={tier.cta_text || tier.cta || ''} onChange={(e) => updateTier(i, 'cta_text', e.target.value)} />
            <Input label="CTA Link" value={tier.cta_link || tier.ctaLink || ''} onChange={(e) => updateTier(i, 'cta_link', e.target.value)} />
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={tierIsFeatured(tier)} onChange={(e) => setFeatured(i, e.target.checked)} className="w-4 h-4 text-blue-600 border-gray-300 rounded" />
            <span className="text-sm text-gray-700">Highlighted (recommended)</span>
          </label>
        </div>
      ))}
      <Button variant="ghost" size="sm" onClick={addTier}>
        <Plus className="h-4 w-4 mr-1" aria-hidden="true" />
        Add Tier
      </Button>
    </div>
  );
}

function CourseHighlightConfig({ config, onChange }: { config: any; onChange: (c: any) => void }) {
  return (
    <div className="space-y-4">
      <Input label="Course Name" value={config.course_name || ''} onChange={(e) => onChange({ ...config, course_name: e.target.value })} />
      <Textarea label="Description" value={config.description || ''} onChange={(e) => onChange({ ...config, description: e.target.value })} rows={3} />
      <ImageUploaderWithAI label="Course Image" value={config.image_url || ''} onChange={(url) => onChange({ ...config, image_url: url })} specKey="course_highlight.image_url" />
      <Input label="Duration" value={config.duration || ''} onChange={(e) => onChange({ ...config, duration: e.target.value })} placeholder="e.g. 6 weeks" />
      <Input label="Level" value={config.level || ''} onChange={(e) => onChange({ ...config, level: e.target.value })} placeholder="e.g. Beginner" />
      <Input label="CTA Text" value={config.cta_text || ''} onChange={(e) => onChange({ ...config, cta_text: e.target.value })} />
      <Input label="CTA Link" value={config.cta_link || ''} onChange={(e) => onChange({ ...config, cta_link: e.target.value })} />
    </div>
  );
}

function InstructorBiosConfig({ config, onChange }: { config: any; onChange: (c: any) => void }) {
  const instructors: any[] = config.instructors || [];

  function updateInstructor(index: number, field: string, value: string) {
    const newItems = [...instructors];
    newItems[index] = { ...newItems[index], [field]: value };
    onChange({ ...config, instructors: newItems });
  }

  function addInstructor() {
    onChange({ ...config, instructors: [...instructors, { name: '', title: '', bio: '', image_url: '' }] });
  }

  function removeInstructor(index: number) {
    onChange({ ...config, instructors: instructors.filter((_, i) => i !== index) });
  }

  return (
    <div className="space-y-4">
      {instructors.map((inst: any, i: number) => (
        <div key={i} className="border border-gray-200 rounded-lg p-4 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">Instructor {i + 1}</span>
            <Button variant="ghost" size="sm" onClick={() => removeInstructor(i)} aria-label={`Remove instructor ${i + 1}`}>
              <Trash2 className="h-4 w-4 text-red-500" aria-hidden="true" />
            </Button>
          </div>
          <Input label="Name" value={inst.name || ''} onChange={(e) => updateInstructor(i, 'name', e.target.value)} />
          <Input label="Title / Role" value={inst.title || ''} onChange={(e) => updateInstructor(i, 'title', e.target.value)} />
          <Textarea label="Bio" value={inst.bio || ''} onChange={(e) => updateInstructor(i, 'bio', e.target.value)} rows={3} />
          <ImageUploaderWithAI label="Photo" value={inst.image_url || ''} onChange={(url) => updateInstructor(i, 'image_url', url)} specKey="instructor_bios.image_url" />
        </div>
      ))}
      <Button variant="ghost" size="sm" onClick={addInstructor}>
        <Plus className="h-4 w-4 mr-1" aria-hidden="true" />
        Add Instructor
      </Button>
    </div>
  );
}

function FAQConfig({ config, onChange }: { config: any; onChange: (c: any) => void }) {
  const items: any[] = config.items || [];

  function updateItem(index: number, field: string, value: string) {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], [field]: value };
    onChange({ ...config, items: newItems });
  }

  function addItem() {
    onChange({ ...config, items: [...items, { question: '', answer: '' }] });
  }

  function removeItem(index: number) {
    onChange({ ...config, items: items.filter((_, i) => i !== index) });
  }

  return (
    <div className="space-y-4">
      {items.map((item: any, i: number) => (
        <div key={i} className="border border-gray-200 rounded-lg p-4 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">Question {i + 1}</span>
            <Button variant="ghost" size="sm" onClick={() => removeItem(i)} aria-label={`Remove question ${i + 1}`}>
              <Trash2 className="h-4 w-4 text-red-500" aria-hidden="true" />
            </Button>
          </div>
          <Input label="Question" value={item.question || ''} onChange={(e) => updateItem(i, 'question', e.target.value)} />
          <Textarea label="Answer" value={item.answer || ''} onChange={(e) => updateItem(i, 'answer', e.target.value)} rows={3} />
        </div>
      ))}
      <Button variant="ghost" size="sm" onClick={addItem}>
        <Plus className="h-4 w-4 mr-1" aria-hidden="true" />
        Add Question
      </Button>
    </div>
  );
}

function StatsCounterConfig({ config, onChange }: { config: any; onChange: (c: any) => void }) {
  const stats: any[] = config.stats || [];

  function updateStat(index: number, field: string, value: string) {
    const newStats = [...stats];
    newStats[index] = { ...newStats[index], [field]: value };
    onChange({ ...config, stats: newStats });
  }

  // The storefront reads one string, `number` ("10k+", "99.9%", "5 min"), and
  // works out any count-up prefix/suffix itself. This editor used to write
  // `value` / `prefix` / `suffix`, which nothing rendered, so the figure is
  // saved as `number` and the three-part keys are retired on first edit.
  function setNumber(index: number, value: string) {
    const newStats = [...stats];
    const { value: _v, prefix: _p, suffix: _s, ...rest } = newStats[index];
    newStats[index] = { ...rest, number: value };
    onChange({ ...config, stats: newStats });
  }

  function addStat() {
    onChange({ ...config, stats: [...stats, { number: '', label: '' }] });
  }

  function removeStat(index: number) {
    onChange({ ...config, stats: stats.filter((_, i) => i !== index) });
  }

  return (
    <div className="space-y-4">
      {stats.map((stat: any, i: number) => (
        <div key={i} className="border border-gray-200 rounded-lg p-4 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">Stat {i + 1}</span>
            <Button variant="ghost" size="sm" onClick={() => removeStat(i)} aria-label={`Remove stat ${i + 1}`}>
              <Trash2 className="h-4 w-4 text-red-500" aria-hidden="true" />
            </Button>
          </div>
          <Input label="Value" value={statNumber(stat)} onChange={(e) => setNumber(i, e.target.value)} placeholder="e.g. 10,000+ or 99.9%" helperText="Shown as typed. A whole number counts up when it scrolls into view." />
          <Input label="Label" value={stat.label || ''} onChange={(e) => updateStat(i, 'label', e.target.value)} placeholder="e.g. Students Enrolled" />
        </div>
      ))}
      <Button variant="ghost" size="sm" onClick={addStat}>
        <Plus className="h-4 w-4 mr-1" aria-hidden="true" />
        Add Stat
      </Button>
    </div>
  );
}

function TrustBadgesConfig({ config, onChange }: { config: any; onChange: (c: any) => void }) {
  const badges: any[] = config.badges || [];

  function updateBadge(index: number, field: string, value: string) {
    const newBadges = [...badges];
    newBadges[index] = { ...newBadges[index], [field]: value };
    onChange({ ...config, badges: newBadges });
  }

  function addBadge() {
    onChange({ ...config, badges: [...badges, { label: '', icon: '', image_url: '' }] });
  }

  function removeBadge(index: number) {
    onChange({ ...config, badges: badges.filter((_, i) => i !== index) });
  }

  return (
    <div className="space-y-4">
      {badges.map((badge: any, i: number) => (
        <div key={i} className="border border-gray-200 rounded-lg p-4 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">Badge {i + 1}</span>
            <Button variant="ghost" size="sm" onClick={() => removeBadge(i)} aria-label={`Remove badge ${i + 1}`}>
              <Trash2 className="h-4 w-4 text-red-500" aria-hidden="true" />
            </Button>
          </div>
          <Input label="Label" value={badge.label || ''} onChange={(e) => updateBadge(i, 'label', e.target.value)} />
          <Input label="Icon Name" value={badge.icon || ''} onChange={(e) => updateBadge(i, 'icon', e.target.value)} helperText="Lucide icon name (e.g. Shield, Award)" />
          <ImageUploaderWithAI label="Badge Image (optional)" value={badge.image_url || ''} onChange={(url) => updateBadge(i, 'image_url', url)} specKey="trust_badges.image_url" />
        </div>
      ))}
      <Button variant="ghost" size="sm" onClick={addBadge}>
        <Plus className="h-4 w-4 mr-1" aria-hidden="true" />
        Add Badge
      </Button>
    </div>
  );
}

function LearningPathConfig({ config, onChange }: { config: any; onChange: (c: any) => void }) {
  const steps: any[] = config.steps || [];

  function updateStep(index: number, field: string, value: string) {
    const newSteps = [...steps];
    newSteps[index] = { ...newSteps[index], [field]: value };
    onChange({ ...config, steps: newSteps });
  }

  function addStep() {
    onChange({ ...config, steps: [...steps, { title: '', description: '', icon: '' }] });
  }

  function removeStep(index: number) {
    onChange({ ...config, steps: steps.filter((_, i) => i !== index) });
  }

  return (
    <div className="space-y-4">
      {steps.map((step: any, i: number) => (
        <div key={i} className="border border-gray-200 rounded-lg p-4 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">Step {i + 1}</span>
            <Button variant="ghost" size="sm" onClick={() => removeStep(i)} aria-label={`Remove step ${i + 1}`}>
              <Trash2 className="h-4 w-4 text-red-500" aria-hidden="true" />
            </Button>
          </div>
          <Input label="Title" value={step.title || ''} onChange={(e) => updateStep(i, 'title', e.target.value)} />
          <Textarea label="Description" value={step.description || ''} onChange={(e) => updateStep(i, 'description', e.target.value)} rows={2} />
          <Input label="Icon Name" value={step.icon || ''} onChange={(e) => updateStep(i, 'icon', e.target.value)} helperText="Lucide icon name (optional)" />
        </div>
      ))}
      <Button variant="ghost" size="sm" onClick={addStep}>
        <Plus className="h-4 w-4 mr-1" aria-hidden="true" />
        Add Step
      </Button>
    </div>
  );
}

function VideoShowcaseConfig({ config, onChange }: { config: any; onChange: (c: any) => void }) {
  return (
    <div className="space-y-4">
      <Input label="Video URL" value={config.video_url || ''} onChange={(e) => onChange({ ...config, video_url: e.target.value })} placeholder="https://youtube.com/embed/..." />
      <ImageUploaderWithAI label="Thumbnail Image" value={config.thumbnail_url || ''} onChange={(url) => onChange({ ...config, thumbnail_url: url })} specKey="video_showcase.thumbnail_url" />
      <Textarea label="Description" value={config.description || ''} onChange={(e) => onChange({ ...config, description: e.target.value })} rows={3} />
      <Input label="CTA Text" value={config.cta_text || ''} onChange={(e) => onChange({ ...config, cta_text: e.target.value })} helperText="Optional" />
      <Input label="CTA Link" value={config.cta_link || ''} onChange={(e) => onChange({ ...config, cta_link: e.target.value })} helperText="Optional" />
    </div>
  );
}

function FeatureComparisonConfig({ config, onChange }: { config: any; onChange: (c: any) => void }) {
  const features: any[] = config.features || [];

  function updateFeature(index: number, field: string, value: any) {
    const newFeatures = [...features];
    newFeatures[index] = { ...newFeatures[index], [field]: value };
    onChange({ ...config, features: newFeatures });
  }

  function addFeature() {
    onChange({ ...config, features: [...features, { name: '', basic: false, pro: false, premium: false }] });
  }

  function removeFeature(index: number) {
    onChange({ ...config, features: features.filter((_, i) => i !== index) });
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-3">
        <Input label="Column 1 Label" value={config.col1_label || ''} onChange={(e) => onChange({ ...config, col1_label: e.target.value })} placeholder="Basic" />
        <Input label="Column 2 Label" value={config.col2_label || ''} onChange={(e) => onChange({ ...config, col2_label: e.target.value })} placeholder="Pro" />
        <Input label="Column 3 Label" value={config.col3_label || ''} onChange={(e) => onChange({ ...config, col3_label: e.target.value })} placeholder="Premium" />
      </div>
      {features.map((feat: any, i: number) => (
        <div key={i} className="border border-gray-200 rounded-lg p-4 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">Feature {i + 1}</span>
            <Button variant="ghost" size="sm" onClick={() => removeFeature(i)} aria-label={`Remove feature ${i + 1}`}>
              <Trash2 className="h-4 w-4 text-red-500" aria-hidden="true" />
            </Button>
          </div>
          <Input label="Feature Name" value={feat.name || ''} onChange={(e) => updateFeature(i, 'name', e.target.value)} />
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={feat.col1 || false} onChange={(e) => updateFeature(i, 'col1', e.target.checked)} className="w-4 h-4" />
              <span className="text-sm">{config.col1_label || 'Col 1'}</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={feat.col2 || false} onChange={(e) => updateFeature(i, 'col2', e.target.checked)} className="w-4 h-4" />
              <span className="text-sm">{config.col2_label || 'Col 2'}</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={feat.col3 || false} onChange={(e) => updateFeature(i, 'col3', e.target.checked)} className="w-4 h-4" />
              <span className="text-sm">{config.col3_label || 'Col 3'}</span>
            </label>
          </div>
        </div>
      ))}
      <Button variant="ghost" size="sm" onClick={addFeature}>
        <Plus className="h-4 w-4 mr-1" aria-hidden="true" />
        Add Feature
      </Button>
    </div>
  );
}

function CTABannerConfig({ config, onChange }: { config: any; onChange: (c: any) => void }) {
  const BG_OPTIONS = [
    { label: 'Sage 700', value: 'bg-sage-700' },
    { label: 'Sage 800', value: 'bg-sage-800' },
    { label: 'Sage 900', value: 'bg-sage-900' },
    { label: 'Terra', value: 'bg-terra' },
    { label: 'Amber 700', value: 'bg-amber-700' },
    { label: 'Stone 800', value: 'bg-stone-800' },
    { label: 'Blue 700', value: 'bg-blue-700' },
    { label: 'Indigo 700', value: 'bg-indigo-700' },
  ];

  return (
    <div className="space-y-4">
      <Textarea label="Body Text" value={config.body || ''} onChange={(e) => onChange({ ...config, body: e.target.value })} rows={3} />
      <Select label="Background Color" value={config.bg_color || 'bg-sage-700'} onChange={(e) => onChange({ ...config, bg_color: e.target.value })} options={BG_OPTIONS} />
      <Input label="CTA Text" value={config.cta_text || ''} onChange={(e) => onChange({ ...config, cta_text: e.target.value })} />
      <Input label="CTA Link" value={config.cta_link || ''} onChange={(e) => onChange({ ...config, cta_link: e.target.value })} />
      <Input label="Secondary CTA Text" value={config.secondary_cta_text || ''} onChange={(e) => onChange({ ...config, secondary_cta_text: e.target.value })} helperText="Optional" />
      <Input label="Secondary CTA Link" value={config.secondary_cta_link || ''} onChange={(e) => onChange({ ...config, secondary_cta_link: e.target.value })} helperText="Optional" />
    </div>
  );
}

function ProcessStepsConfig({ config, onChange }: { config: any; onChange: (c: any) => void }) {
  const steps: any[] = config.steps || [];

  function updateStep(index: number, field: string, value: string) {
    const newSteps = [...steps];
    newSteps[index] = { ...newSteps[index], [field]: value };
    onChange({ ...config, steps: newSteps });
  }

  function addStep() {
    onChange({ ...config, steps: [...steps, { title: '', description: '', icon: '' }] });
  }

  function removeStep(index: number) {
    onChange({ ...config, steps: steps.filter((_, i) => i !== index) });
  }

  return (
    <div className="space-y-4">
      <Select
        label="Layout"
        value={config.layout || 'horizontal'}
        onChange={(e) => onChange({ ...config, layout: e.target.value })}
        options={[
          { label: 'Horizontal', value: 'horizontal' },
          { label: 'Vertical', value: 'vertical' },
        ]}
      />
      {steps.map((step: any, i: number) => (
        <div key={i} className="border border-gray-200 rounded-lg p-4 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">Step {i + 1}</span>
            <Button variant="ghost" size="sm" onClick={() => removeStep(i)} aria-label={`Remove step ${i + 1}`}>
              <Trash2 className="h-4 w-4 text-red-500" aria-hidden="true" />
            </Button>
          </div>
          <Input label="Title" value={step.title || ''} onChange={(e) => updateStep(i, 'title', e.target.value)} />
          <Textarea label="Description" value={step.description || ''} onChange={(e) => updateStep(i, 'description', e.target.value)} rows={2} />
          <Input label="Icon Name" value={step.icon || ''} onChange={(e) => updateStep(i, 'icon', e.target.value)} helperText="Lucide icon name (optional)" />
        </div>
      ))}
      <Button variant="ghost" size="sm" onClick={addStep}>
        <Plus className="h-4 w-4 mr-1" aria-hidden="true" />
        Add Step
      </Button>
    </div>
  );
}

interface AppRow {
  id: string;
  client_id: string;
  name: string;
  base_url: string;
  is_active: boolean;
}

/**
 * Config editor for the `app_embed` block — lets the admin pick from
 * the installed apps (dropdown), enter a path on that app, and
 * optionally pin a height. Only `app_id` is persisted; the renderer
 * resolves `base_url` at render time via the public `/apps/:appId`
 * endpoint so a hosting-domain change doesn't leave stale CMS rows.
 */
function AppEmbedConfig({ config, onChange }: { config: any; onChange: (c: any) => void }) {
  const [apps, setApps] = useState<AppRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await api.get<{ apps: AppRow[] }>('/admin/apps');
        if (!cancelled) setApps(res.apps.filter((a) => a.is_active));
      } catch (e) {
        if (!cancelled) setLoadError(e instanceof Error ? e.message : 'Failed to load apps');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const selected = apps.find((a) => a.id === config.app_id);

  function onSelect(id: string) {
    const { base_url: _drop, ...rest } = config ?? {};
    onChange({
      ...rest,
      app_id: id,
    });
  }

  if (loading) return <p className="text-sm text-stone-500">Loading installed apps…</p>;
  if (loadError) return <p className="text-sm text-red-600">{loadError}</p>;
  if (apps.length === 0) {
    return (
      <p className="text-sm text-stone-600">
        No apps installed yet. Install one from <span className="font-mono">/admin/apps</span> first.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      <Select
        label="App"
        value={config.app_id || ''}
        onChange={(e) => onSelect(e.target.value)}
        options={[
          { label: 'Select an app…', value: '' },
          ...apps.map((a) => ({ label: `${a.name} (${a.base_url})`, value: a.id })),
        ]}
      />
      <Input
        label="Path on the app"
        value={config.path || '/'}
        onChange={(e) => onChange({ ...config, path: e.target.value })}
        placeholder="/meal-plans"
        helperText="Path on the app to render (e.g. /, /meal-plans, /dashboard)"
      />
      <Input
        label="Height (px, optional)"
        type="number"
        value={config.height ?? ''}
        onChange={(e) => {
          const raw = e.target.value;
          onChange({ ...config, height: raw === '' ? undefined : Number(raw) });
        }}
        placeholder="600"
        helperText="Leave blank to let the app auto-resize via postMessage"
      />
      {selected && (
        <p className="text-xs text-stone-500">
          Embedding <span className="font-mono">{selected.base_url}{config.path || '/'}</span>
        </p>
      )}
    </div>
  );
}

function RawJsonConfig({ config, onChange }: { config: any; onChange: (c: any) => void }) {
  const [jsonStr, setJsonStr] = useState(JSON.stringify(config, null, 2));
  const [jsonError, setJsonError] = useState('');

  function handleChange(value: string) {
    setJsonStr(value);
    try {
      const parsed = JSON.parse(value);
      setJsonError('');
      onChange(parsed);
    } catch {
      setJsonError('Invalid JSON');
    }
  }

  return (
    <div>
      <Textarea
        label="Content Configuration (JSON)"
        value={jsonStr}
        onChange={(e) => handleChange(e.target.value)}
        rows={8}
        error={jsonError}
        helperText="Raw JSON configuration"
      />
    </div>
  );
}

export default function SectionConfigEditor({ sectionType, config, onChange }: SectionConfigEditorProps) {
  switch (sectionType) {
    case 'hero':
      return <HeroConfig config={config} onChange={onChange} />;
    case 'story':
      return <StoryConfig config={config} onChange={onChange} />;
    case 'image_banner':
      return <ImageBannerConfig config={config} onChange={onChange} />;
    case 'featured_products':
    case 'new_arrivals':
      return <FeaturedProductsConfig config={config} onChange={onChange} />;
    case 'seasonal_spotlight':
      return <SeasonalSpotlightConfig config={config} onChange={onChange} />;
    case 'value_props':
      return <ValuePropsConfig config={config} onChange={onChange} />;
    case 'image_gallery':
      return <ImageGalleryConfig config={config} onChange={onChange} />;
    case 'banner':
      return <BannerConfig config={config} onChange={onChange} />;
    case 'growing_tips':
      return <GrowingTipsConfig config={config} onChange={onChange} />;
    case 'testimonials':
      return <TestimonialsConfig config={config} onChange={onChange} />;
    case 'pricing_tiers':
      return <PricingTiersConfig config={config} onChange={onChange} />;
    case 'course_highlight':
      return <CourseHighlightConfig config={config} onChange={onChange} />;
    case 'instructor_bios':
      return <InstructorBiosConfig config={config} onChange={onChange} />;
    case 'faq':
      return <FAQConfig config={config} onChange={onChange} />;
    case 'stats_counter':
      return <StatsCounterConfig config={config} onChange={onChange} />;
    case 'trust_badges':
      return <TrustBadgesConfig config={config} onChange={onChange} />;
    case 'learning_path':
      return <LearningPathConfig config={config} onChange={onChange} />;
    case 'video_showcase':
      return <VideoShowcaseConfig config={config} onChange={onChange} />;
    case 'feature_comparison':
      return <FeatureComparisonConfig config={config} onChange={onChange} />;
    case 'cta_banner':
      return <CTABannerConfig config={config} onChange={onChange} />;
    case 'process_steps':
      return <ProcessStepsConfig config={config} onChange={onChange} />;
    case 'app_embed':
      return <AppEmbedConfig config={config} onChange={onChange} />;
    default:
      return <RawJsonConfig config={config} onChange={onChange} />;
  }
}
